/**
 * Grid Component
 *
 * Renders a 10x10 battleship grid with:
 * - Coordinate labels (A-J columns, 1-10 rows)
 * - Interactive cells with click handlers
 * - Visual state management (hit, miss, untouched)
 * - Responsive styling
 */

import GridCell from './GridCell';

const COLUMNS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
const ROWS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function Grid({
  onCellClick,
  onCellHover,
  onCellLeave,
  attackedCells = {},
  isClickable = true,
  pendingAttack = null,
}) {
  /**
   * Generates all cell coordinates for the 10x10 grid
   */
  const generateCells = () => {
    const cells = [];
    for (const row of ROWS) {
      for (const col of COLUMNS) {
        const coordinate = `${col}${row}`;
        let state = attackedCells[coordinate] || 'untouched';

        // Show attacking state for pending attack
        if (pendingAttack === coordinate && state === 'untouched') {
          state = 'attacking';
        }

        cells.push({
          coordinate,
          state,
        });
      }
    }
    return cells;
  };

  const cells = generateCells();

  /**
   * Handle cell click with validation
   */
  const handleClick = (coordinate, state) => {
    // Don't allow clicks on already attacked cells
    if (state !== 'untouched') {
      return;
    }

    // Call parent handler if provided
    if (onCellClick) {
      onCellClick(coordinate);
    }
  };

  return (
    <div className="flex flex-col items-center">
      {/* Column Labels */}
      <div className="flex ml-8 mb-1">
        {COLUMNS.map((col) => (
          <div
            key={col}
            data-testid={`column-label-${col}`}
            className="w-11 h-6 flex items-center justify-center text-sm font-semibold text-neon-cyan"
          >
            {col}
          </div>
        ))}
      </div>

      {/* Grid with Row Labels */}
      <div className="flex">
        {/* Row Labels */}
        <div className="flex flex-col mr-1">
          {ROWS.map((row) => (
            <div
              key={row}
              data-testid={`row-label-${row}`}
              className="w-6 h-11 flex items-center justify-center text-sm font-semibold text-neon-cyan"
            >
              {row}
            </div>
          ))}
        </div>

        {/* Grid Container */}
        <div
          data-testid="grid-container"
          className="grid grid-cols-10 gap-0 border-2 border-neon-cyan shadow-glow-cyan"
        >
          {cells.map(({ coordinate, state }) => (
            <GridCell
              key={coordinate}
              coordinate={coordinate}
              state={state}
              onClick={() => handleClick(coordinate, state)}
              onMouseEnter={onCellHover}
              onMouseLeave={onCellLeave}
              isClickable={isClickable}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
